import { Component, effect, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { SesionApiService } from '@core/services/api/sesion.service';
import { AppService } from '@shared/services/app.service';
import { ToastService } from '@shared/services/toast.service';
import { AuthStateService } from '@shared/services/auth-state.service';
import { AuthApiService } from '@core/services/api/auth.service';
import { Router } from '@angular/router';
import { Tabla } from '@shared/components/tabla/tabla';
import { ITablaColumna } from '@core/interfaces/tabla.interface';
import { UiButton, UiDialog, UiTooltip } from '@shared/components/ui';
import { DatePipe } from '@angular/common';
import { SucursalService } from '@shared/services/sucursal.service';
import type { ISesion } from '@core/services/api/sesion.service';

interface SesionRow {
  id: number;
  dispositivo: string;
  navegador: string;
  so: string;
  estado: string;
  estado_activo: boolean;
  ultima_actividad: string;
  inicio: string;
}

@Component({
  selector: 'app-mis-sesiones',
  imports: [Tabla, UiButton, UiDialog, UiTooltip],
  templateUrl: './mis-sesiones.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './mis-sesiones.css',
})
export class MisSesiones implements OnInit {
  constructor() {
    effect(
      () => {
        if (this.sucursalService.sucursalReady() && this.appService.authReady()) {
          this.cargarSesiones();
        }
      },
      { allowSignalWrites: true },
    );
  }

  private readonly sesionApi = inject(SesionApiService);
  private readonly appService = inject(AppService);
  private readonly toast = inject(ToastService);
  private readonly authState = inject(AuthStateService);
  private readonly authApi = inject(AuthApiService);
  private readonly sucursalService = inject(SucursalService);
  private readonly router = inject(Router);
  private readonly datePipe = new DatePipe('es-PE');

  sesiones = signal<SesionRow[]>([]);
  loading = signal(true);
  modalCerrarTodas = signal(false);
  columnas: ITablaColumna[] = [];

  ngOnInit(): void {
    this.appService.setPanelHeader({
      title: 'Mis Sesiones',
      subtitle: 'Gestiona los dispositivos donde has iniciado sesion.',
      showBackButton: true,
      icon: 'bi bi-shield-lock',
    });
    this.columnas = [
      { key: 'dispositivo', titulo: 'Dispositivo' },
      { key: 'navegador', titulo: 'Navegador' },
      { key: 'so', titulo: 'Sistema Operativo' },
      { key: 'estado', titulo: 'Estado' },
      { key: 'ultima_actividad', titulo: 'Ultima actividad' },
      { key: 'inicio', titulo: 'Inicio' },
    ];
  }

  cargarSesiones() {
    this.loading.set(true);
    this.sesionApi.obtenerMisSesiones().subscribe({
      next: (data) => {
        const rows = this.mapRows(data);
        this.sesiones.set(rows);
        this.loading.set(false);
        if (rows.length > 0 && rows.every((r) => !r.estado_activo)) {
          this.authApi.logout().subscribe({
            complete: () => {
              this.authState.clearSession();
              this.router.navigate(['/home']);
            },
            error: () => {
              this.authState.clearSession();
              this.router.navigate(['/home']);
            },
          });
        }
      },
      error: (err) => {
        if (err.status === 401) return;
        this.toast.show(
          'top-right',
          'error',
          'Error',
          err.error?.message || 'No se pudieron cargar las sesiones.',
        );
        this.loading.set(false);
      },
    });
  }

  cerrarSesion(sesion: SesionRow) {
    this.sesionApi.cerrarMiSesion(sesion.id).subscribe({
      next: () => {
        this.toast.show(
          'top-right',
          'success',
          'Sesion cerrada',
          'La sesion fue cerrada exitosamente.',
        );
        this.cargarSesiones();
      },
      error: (err) =>
        this.toast.show(
          'top-right',
          'error',
          'Error',
          err.error?.message || 'No se pudo cerrar la sesion.',
        ),
    });
  }

  cerrarOtrasSesiones(sesionActual: SesionRow) {
    this.sesionApi.cerrarOtrasSesiones(sesionActual.id).subscribe({
      next: () => {
        this.toast.show(
          'top-right',
          'success',
          'Sesiones cerradas',
          'Todas las demas sesiones fueron cerradas.',
        );
        this.cargarSesiones();
      },
      error: (err) =>
        this.toast.show(
          'top-right',
          'error',
          'Error',
          err.error?.message || 'No se pudieron cerrar las sesiones.',
        ),
    });
  }

  confirmarCerrarTodas() {
    this.modalCerrarTodas.set(true);
  }

  cerrarTodasSesiones() {
    this.modalCerrarTodas.set(false);
    this.sesionApi.cerrarTodasSesiones().subscribe({
      next: () => {
        this.authApi.logout().subscribe({
          complete: () => {
            this.authState.clearSession();
            this.router.navigate(['/home']);
          },
        });
      },
      error: (err) =>
        this.toast.show(
          'top-right',
          'error',
          'Error',
          err.error?.message || 'No se pudieron cerrar las sesiones.',
        ),
    });
  }

  private mapRows(data: ISesion[]): SesionRow[] {
    return data.map((s) => ({
      id: s.id,
      dispositivo: s.device_name,
      navegador: s.browser,
      so: s.operating_system,
      estado: s.is_active ? 'Activa' : 'Cerrada',
      estado_activo: s.is_active,
      ultima_actividad: this.formatDate(s.last_activity),
      inicio: this.formatDate(s.created_at),
    }));
  }

  private formatDate(dateStr: string | null): string {
    if (!dateStr) return '-';
    return this.datePipe.transform(dateStr, 'dd/MM/yy HH:mm') ?? '-';
  }
}
